import UploadSalesOrderAttachment from 'components/commission-management/sales-order/UploadSalesOrderAttachment';
import Loader from 'components/layout/Loader';
import { SALES_ORDER_PERMISSIONS } from 'constants/commission-management-permissions';
import AuthContext from 'contexts/AuthContext';
import useCommissionPermissions from 'hooks/useCommissionPermissions';
import useFeedback from 'hooks/useFeedback';
import { Dialog } from 'primereact/dialog';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Table, Button } from 'reactstrap';
import {
  deleteSalesOrderAttachment,
  fetchSalesOrderAttachments,
  insertSalesOrderAttachment,
  updateSalesOrderAttachment,
} from 'services/api/sales-order-attachments';
import { fetchSalesOrderAttachmentTypes } from 'services/api/sales-order-attachments-types';

const STYLES = {
  truncateCellText: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
};

const useParseCustomResponse = () => {
  const { logout } = useContext(AuthContext);
  const { error } = useFeedback();

  const parseCustomResponse = useCallback(
    (response) => {
      if (response.success) {
        return { success: true, data: response.data };
      }

      if (response.authenticated === false) {
        logout();
      }

      if (response.authorized === false) {
        error('Non sei autorizzato a vedere questa pagina');
        return { success: false, message: 'Non sei autorizzato a vedere questa pagina' };
      }

      error(response.message);
      return { success: false, message: response.message };
    },
    [error, logout]
  );

  return parseCustomResponse;
};

const useRetrieveData = (salesOrderId) => {
  const [loading, setLoading] = useState(true);
  const [response, setResponse] = useState();

  const { error } = useFeedback();
  const parseCustomResponse = useParseCustomResponse();

  const fetchData = useCallback(async () => {
    const [AttachmentRawTypes, AttachmentsRaw] = await Promise.all([
      fetchSalesOrderAttachmentTypes(),
      fetchSalesOrderAttachments(salesOrderId),
    ]);

    return {
      AttachmentRawTypes,
      AttachmentsRaw,
    };
  }, [salesOrderId]);

  useEffect(() => {
    setLoading(true);
    fetchData()
      .then(({ AttachmentRawTypes, AttachmentsRaw }) => {
        const attachmentParsedResponse = parseCustomResponse(AttachmentsRaw);
        setResponse({
          attachmentTypesResponse: parseCustomResponse(AttachmentRawTypes),
          attachmentResponse: attachmentParsedResponse.data.attachments,
          editableResponse: attachmentParsedResponse.data.editable,
        });
      })
      .catch(() => {
        error('Errore durante il download dei dati');
        setLoading(false);
      })
      .finally(() => setLoading(false));
  }, [fetchData, parseCustomResponse, error]);

  return { loading, response };
};

const PreviewDocuments = ({ attachments, onDeleteAttachment, salesOrderId, canDownload, canEdit, onShowModal }) => {
  return (
    <Table responsive bordered style={{ tableLayout: 'fixed' }}>
      <thead>
        <tr>
          <th className="align-middle">Tipo documento</th>
          <th className="align-middle">Nome del file</th>
          <th className="align-middle">Nota</th>
          <th className="align-middle" style={{ width: '60px' }}></th>
          <th className="align-middle" style={{ width: '60px' }}></th>
          <th className="align-middle" style={{ width: '100px' }}></th>
        </tr>
      </thead>
      <tbody>
        {attachments?.map((attachment) => (
          <PreviewDocument
            key={`attachment-${attachment.attachmentId}`}
            attachment={attachment}
            onDeleteAttachment={onDeleteAttachment}
            canEdit={canEdit}
            salesOrderId={salesOrderId}
            canDownload={canDownload}
            onShowModal={onShowModal}
          />
        ))}
      </tbody>
    </Table>
  );
};

const PreviewDocument = ({ attachment, onDeleteAttachment, canEdit, salesOrderId, canDownload, onShowModal }) => {
  const deleteHandler = () => {
    onDeleteAttachment(attachment.attachmentId);
  };

  const showModalHandler = () => {
    onShowModal?.(attachment.attachmentId);
  };

  return (
    <tr>
      <td className="align-middle" style={STYLES.truncateCellText} title={attachment.description}>
        {attachment.description}
      </td>
      <td className="align-middle" style={STYLES.truncateCellText} title={attachment.name}>
        {attachment.name}
      </td>
      <td className="align-middle" style={STYLES.truncateCellText} title={attachment.note}>
        {attachment.note}
      </td>
      <td className="align-middle text-center">
        {canEdit && (
          <Button
            color="primary"
            onClick={showModalHandler}
            size="sm"
            title={`Modifica ${attachment.description.toLowerCase()}`}
          >
            <i className="fa fa-pen" />
          </Button>
        )}
      </td>
      <td className="align-middle text-center">
        {canEdit && (
          <Button
            color="danger"
            onClick={deleteHandler}
            size="sm"
            title={`Cancella ${attachment.description.toLowerCase()}`}
          >
            <i className="fa fa-trash" />
          </Button>
        )}
      </td>
      <td className="align-middle text-center">
        {canDownload && (
          <a
            target="_blank"
            rel="noreferrer"
            href={`/api/sales-order/${salesOrderId}/attachments/download/${attachment.attachmentId}`}
            className="small"
            title={`Visualizza ${attachment.description.toLowerCase()}`}
          >
            Visualizza
          </a>
        )}
      </td>
    </tr>
  );
};

const SalesOrderAttachmentsPage = () => {
  const [formId, setFormId] = useState(0);
  const [attachments, setAttachments] = useState([]);
  const [editable, setEditable] = useState();

  const [isLoading, setIsLoading] = useState(false);
  const { salesOrderId } = useParams();
  const parseCustomResponse = useParseCustomResponse();
  const { hasPermission } = useCommissionPermissions();
  const { success } = useFeedback();
  const { loading, response } = useRetrieveData(salesOrderId);
  const [document, setDocument] = useState();
  const [isModalAttachmentVisible, setIsModalAttachmentVisible] = useState(false);

  const canEdit = editable && hasPermission(SALES_ORDER_PERMISSIONS.ATTACHMENTS.EDIT);
  const canDownload = hasPermission(SALES_ORDER_PERMISSIONS.ATTACHMENTS.DOWNLOAD);

  const showDocumentModal = (attachmentId) => {
    setDocument(attachments.find((x) => x.attachmentId === attachmentId));
    setIsModalAttachmentVisible(true);
  };

  const handleOnCreateAttachmentFormSubmit = (formData, data, attachmentTypeDescription) => {
    setIsLoading(true);
    insertSalesOrderAttachment(salesOrderId, formData)
      .then((response) => {
        const parsedResponse = parseCustomResponse(response);
        if (parsedResponse.success) {
          success("L'allegato è stato aggiunto");
          setAttachments((attachments) => [
            ...attachments,
            {
              attachmentId: parsedResponse.data,
              attachmentTypeId: data.attachmentTypeId,
              description: attachmentTypeDescription.description,
              name: data.file.name,
              attachmentDate: data.attachmentDate,
              note: data.note,
            },
          ]);
          setFormId((previousFormId) => previousFormId + 1);
        }
      })
      .finally(() => setIsLoading(false));
  };

  const handleOnUpdateAttachmentFormSubmit = (formData, data, attachmentTypeDescription) => {
    setIsModalAttachmentVisible(false);
    setIsLoading(true);
    updateSalesOrderAttachment(salesOrderId, document.attachmentId, formData)
      .then((response) => {
        const parsedResponse = parseCustomResponse(response);
        if (parsedResponse.success) {
          success("L'allegato è stato modificato");
          setAttachments((attachments) =>
            attachments.map((doc) =>
              doc.attachmentId === document.attachmentId
                ? {
                    ...doc,
                    attachmentTypeId: data.attachmentTypeId,
                    description: attachmentTypeDescription.description,
                    name: data.file?.name ? data.file?.name : document.name,
                    documentTypeId: data.documentTypeId,
                    note: data.note,
                  }
                : doc
            )
          );
        }
      })
      .finally(() => setIsLoading(false));
  };

  const handleOnDeleteAttachmentClick = (id) => {
    if (window.confirm("Sei sicuro di voler eliminare l'allegato selezionato?")) {
      setIsLoading(true);
      deleteSalesOrderAttachment(salesOrderId, id)
        .then((response) => {
          const parsedResponse = parseCustomResponse(response);
          if (parsedResponse.success) {
            success("L'allegato è stato eliminato");
            setAttachments((attachments) => attachments.filter((doc) => doc.attachmentId !== id));
          }
        })
        .finally(() => setIsLoading(false));
    }
  };

  useEffect(() => {
    if (!response) {
      return;
    }
    setEditable(response.editableResponse);
    setAttachments(response.attachmentResponse);
  }, [response]);

  return (
    <>
      {(loading || isLoading) && <Loader />}
      <Dialog
        header="Modifica documento"
        visible={isModalAttachmentVisible}
        style={{ width: '60vw' }}
        onHide={() => setIsModalAttachmentVisible(false)}
      >
        <UploadSalesOrderAttachment
          attachmentTypes={response?.attachmentTypesResponse?.data.types}
          formName={'Attachment_'}
          formId={formId}
          onSubmit={handleOnUpdateAttachmentFormSubmit}
          document={document}
          isFileRequired={false}
          inline={false}
        />
      </Dialog>
      {attachments.length > 0 && (
        <section>
          <h2>Allegati caricati</h2>
          <PreviewDocuments
            attachments={attachments}
            salesOrderId={salesOrderId}
            canDownload={canDownload}
            canEdit={canEdit}
            onDeleteAttachment={handleOnDeleteAttachmentClick}
            onShowModal={showDocumentModal}
          />
        </section>
      )}
      {canEdit && (
        <section>
          <h2>Carica un nuovo allegato</h2>
          <UploadSalesOrderAttachment
            attachmentTypes={response.attachmentTypesResponse.data.types}
            formName={'Attachment_'}
            formId={formId}
            canEdit={canEdit}
            onSubmit={handleOnCreateAttachmentFormSubmit}
            isFileRequired
            inline
          />
        </section>
      )}
      {!canEdit && attachments.length === 0 && <p>Non ci sono allegati da visualizzare</p>}
    </>
  );
};

export default SalesOrderAttachmentsPage;
